import React, { useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { MathJax } from "better-react-mathjax";
import { RichText } from "@learningblocks/shared-components/lib/RichText";

export const Equation = ({ text }) => {
  const mathJaxRef = useRef(null);

  useEffect(() => {
    if (!mathJaxRef.current) return;

    const container = mathJaxRef.current;
    let observer;

    const removeDuplicateFocusableElements = () => {
      const focusableElements = container.querySelectorAll('[tabindex="0"]');

      focusableElements.forEach((element, index) => {
        if (index > 0) {
          element.setAttribute("tabindex", "-1");
        }
      });
    };

    const initAccessibility = () => {
      removeDuplicateFocusableElements();

      observer = new MutationObserver(() => {
        removeDuplicateFocusableElements();
      });

      observer.observe(container, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ["tabindex"],
      });
    };

    if (window.MathJax?.typesetPromise) {
      window.MathJax.typesetPromise([container]).then(() => {
        initAccessibility();
      });
    } else {
      const timer = setTimeout(initAccessibility, 100);
      return () => clearTimeout(timer);
    }

    return () => {
      if (observer) observer.disconnect();
    };
  }, [text]);

  /**
   * Check if text contains MathJax expressions
   * @param {string} text - Text to check
   * @returns {boolean} True if text contains MathJax expressions
   */
  const containsMathJax = (text) => {
    if (!text || typeof text !== "string") return false;
    // Check for MathJax patterns: $$...$$ or $...$
    return /\$\$.*?\$\$|\$.*?\$/g.test(text);
  };

  const renderMixedContent = (text) => {
    if (!text || typeof text !== "string") return text;

    // Skip if there's no math
    if (!containsMathJax(text)) {
      return <RichText text={text} />;
    }

    // Split text by MathJax expressions while preserving delimiters
    const parts = text.split(/(\$\$.*?\$\$|\$.*?\$)/g).filter(Boolean);

    return (
      <>
        {parts.map((part, index) => {
          if (containsMathJax(part)) {
            // Detect whether it's inline ($...$) or display ($$...$$)
            const isInline = /^\$(?!\$)(.*?)(?<!\$)\$$/.test(part);
            return (
              <MathJax key={index} inline={isInline} dynamic>
                {part}
              </MathJax>
            );
          }
          return <RichText text={part} />;
        })}
      </>
    );
  };

  // Only apply equation-wrapper styling to display equations ($$...$$)
  // Inline equations ($...$) and mixed content should not get block-level styling
  const isDisplayEquation =
    text &&
    typeof text === "string" &&
    text.trim().startsWith("$$") &&
    text.trim().endsWith("$$");

  return isDisplayEquation ? (
    <span ref={mathJaxRef} className="equation-wrapper-scrollable">
      {renderMixedContent(text)}
    </span>
  ) : (
    <>{renderMixedContent(text)}</>
  );
};

Equation.propTypes = {
  type: PropTypes.string,
  content: PropTypes.array,
  text: PropTypes.string.isRequired,
};
